import { ActionCard, LabelColor, notEmpty } from '@arnold/common';
import { getLocale } from '@arnold/core';
import { format } from 'date-fns';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

export enum CardType {
  Channel = 'Channel',
  Report = 'Report',
  ReportSequence = 'ReportSequence',
}
interface IAnswerCard {
  id: string;
  topicName: string;
  from: string;
  to: string;
  answered: boolean;
  answeredAnonymously: boolean;
  reactionsCount: number;
}

const getLink = (id: string, type: CardType, from?: string | null) => {
  switch (type) {
    case CardType.Channel:
      return `/${id}`;
    case CardType.Report:
      return `/results/${id}`;
    case CardType.ReportSequence:
      if (from) {
        return `/process-results/${id}?added_on_from=${encodeURIComponent(from)}&added_on_to=null`;
      }
      return `/process-results/${id}`;
    default:
      throw new Error('Invalid card type - ' + type);
  }
};

export const OverviewAnswersCard = ({ card }: { card: IAnswerCard }) => {
  const { t } = useTranslation('OverviewCard');
  const history = useHistory();
  const reactionsCount = card.reactionsCount;
  const description = `${format(new Date(card.from), 'd. MMMM', { locale: getLocale(i18next.language) })} - ${format(
    new Date(card.to),
    'd. MMMM yyyy',
    { locale: getLocale(i18next.language) },
  )}`;
  const canStillAnswer = !card.answered && new Date(card.to) > new Date();
  const buttonType = !canStillAnswer && (!card.answered || card.answeredAnonymously) ? 'text' : 'button';
  const buttonVariant = card.answered ? 'outline-primary' : 'primary';
  const actionText = card.answered
    ? card.answeredAnonymously
      ? t('answeredAnonymously')
      : t('showChat')
    : canStillAnswer
      ? t('answerToSurvey')
      : t('notAnswered');
  const onClick = () => buttonType === 'button' && history.push(getLink(card.id, CardType.Channel));
  return (
    <ActionCard
      title={t(card.topicName)}
      description={description}
      action={{
        text: actionText,
        type: buttonType,
        buttonVariant,
        onClick,
      }}
      label={
        canStillAnswer
          ? { text: t('notAnswered'), color: LabelColor.DANGER }
          : reactionsCount > 0
            ? {
                text: t('numberOfReactions', { count: reactionsCount, postProcess: 'interval' }),
                color: LabelColor.NEUTRAL,
              }
            : undefined
      }
      markerColor={canStillAnswer ? LabelColor.DANGER : reactionsCount > 0 ? LabelColor.NEUTRAL : undefined}
      dataIcom="answer-card"
    />
  );
};

interface IReportCard {
  id: string;
  topicName: string;
  from: string;
  to: string;
  opened: boolean;
  type: CardType;
  unreadMessages?: number | null;
  reportFrom?: string | null;
}

export const OverviewReportCard = ({ card }: { card: IReportCard }) => {
  const { t } = useTranslation('OverviewCard');
  const history = useHistory();
  const description = `${format(new Date(card.from), 'd. MMMM', { locale: getLocale(i18next.language) })} - ${format(
    new Date(card.to),
    'd. MMMM yyyy',
    { locale: getLocale(i18next.language) },
  )}`;
  const buttonVariant = card.opened ? 'outline-primary' : 'primary';
  const onClick = () => history.push(getLink(card.id, card.type, card.reportFrom));
  return (
    <ActionCard
      title={t(card.topicName)}
      description={description}
      action={{
        type: 'button',
        buttonVariant,
        text: t('showReport'),
        onClick,
      }}
      label={
        !card.opened && notEmpty(card.unreadMessages)
          ? {
              text: t('unreadMessages', { count: card.unreadMessages, postProcess: 'interval' }),
              color: LabelColor.SUCCESS,
            }
          : undefined
      }
      markerColor={!card.opened ? LabelColor.SUCCESS : undefined}
      dataIcom="report-card"
    />
  );
};
